import { Container, Typography } from '@cleartrip/ct-design-components'
import { B2CTheme } from '@cleartrip/ct-design-theme'
import React, { memo } from 'react'

import { srpHostedIcon } from '@/assets/icons/SrpHostedIcon'

import RevampTooltip from './RevampTooltip'
import styles from './styles.module.scss'
import { TUPLE_COLORS, TUPLE_HOVER_UNDERLINE_CSS } from '../config/tupleTheme'
import type { TupleEndpoint } from '../model/types'

export interface TimeRouteBlockProps {
  endpoint: TupleEndpoint
  align?: 'start' | 'end'
  density?: 'default' | 'compact'
}

const CSS_COMPACT_BLOCK = { flex: '0 0 96px', width: 96, minWidth: 96, maxWidth: 96 }
const CSS_DEFAULT_BLOCK = { flex: '0 0 114px', width: 114, minWidth: 114, maxWidth: 114 }
const CSS_GAP2 = { gap: 2 }
const NearbyInfoIcon = srpHostedIcon('info_icon_nearby.svg', 14)
const CSS_NEARBY_TEXT = { color: TUPLE_COLORS.alert500 }
const CSS_END_TIME_ROW = { justifyContent: 'flex-end', width: '100%' }

const TimeRouteBlock: React.FC<TimeRouteBlockProps> = ({ align = 'start', density = 'default', endpoint }) => {
  const name = endpoint.displayName || endpoint.city
  const isNearby = Boolean(endpoint.isNearby)
  const isEnd = align === 'end'
  const isCompact = density === 'compact'
  const sizeCss = isCompact ? CSS_COMPACT_BLOCK : CSS_DEFAULT_BLOCK
  const edge = isEnd ? 'flex-end' : 'flex-start'
  const textAlign = isEnd ? 'right' : 'left'
  const blockCss = { ...sizeCss, alignItems: edge, textAlign, gap: 2 }
  const fitToEdgeCss = { alignItems: edge, alignSelf: edge, maxWidth: '100%', minWidth: 0, width: 'fit-content' }
  const nameRowCss = { ...CSS_GAP2, justifyContent: edge, maxWidth: '100%', minWidth: 0, width: 'fit-content' }
  const anchorStyle = { alignSelf: edge, maxWidth: '100%', minWidth: 0, width: 'fit-content', marginLeft: isEnd ? 'auto' : undefined }
  const nameStyle: React.CSSProperties = { ...(isNearby ? CSS_NEARBY_TEXT : undefined), maxWidth: '100%', minWidth: 0, textAlign }

  const blockClassName = isEnd ? 'flex flex-column flex-end ta-right flex-1' : 'flex flex-column flex-1'
  const timeRowClassName = isEnd ? 'flex flex-baseline flex-cg-1 flex-end' : 'flex flex-baseline flex-cg-1'
  const nameColumnClassName = isEnd ? 'flex flex-column flex-end' : 'flex flex-column'
  const nameRowClassName = isEnd ? 'flex flex-middle flex-end' : 'flex flex-middle'
  const nameClassName = isNearby
    ? `tt-ellipsis o-hidden c-pointer ${styles.onHoverFontWeight}`
    : 'tt-ellipsis o-hidden c-pointer'

  const airportHighlightName = endpoint.airportName || name
  const locationSuffix = endpoint.airportCode ? ` (${endpoint.airportCode})` : ''
  const nearbyCodeSuffix = isNearby && endpoint.airportCode ? ` (${endpoint.airportCode})` : ''
  const nameText = `${name}${nearbyCodeSuffix}`

  let airportLabel = name
  if (endpoint.airportName && endpoint.airportName !== name) {
    airportLabel = `${endpoint.airportName}, ${endpoint.city} (${endpoint.airportCode || name})`
  } else if (endpoint.airportCode && endpoint.airportCode !== name) {
    airportLabel = `${name} (${endpoint.airportCode})`
  }

  const tooltipContent = isNearby ? (
    <Typography color='neutral' componentNode='p' variant='B3'>
      {isEnd ? 'This flight lands at ' : 'This flight departs from '}
      <span style={{ color: TUPLE_COLORS.tooltipHighlight }}>{airportHighlightName}</span>
      {locationSuffix}
    </Typography>
  ) : (
    <Typography className='ws-nowrap' color='neutral' componentNode='p' variant='B3'>
      {airportLabel}
    </Typography>
  )

  return (
    <Container className={blockClassName} css={blockCss}>
      <Container className={timeRowClassName} css={isEnd ? CSS_END_TIME_ROW : undefined}>
        <Typography className='ws-nowrap' color='heading' variant='HM2' css={{ lineHeight: '24px' }}>
          {endpoint.time}
        </Typography>
        {endpoint.arrivalDateLabel ? (
          <Typography className='ws-nowrap' colorCode={B2CTheme.color.border.darkRed} componentNode='p' variant='B3'>
            {endpoint.arrivalDateLabel}
          </Typography>
        ) : null}
      </Container>

      <RevampTooltip
        align='center'
        content={tooltipContent}
        position='bottom'
        style={anchorStyle}
        wrapperDisplay='flex'
      >
        <Container className={nameColumnClassName} css={fitToEdgeCss}>
          <Container className={nameRowClassName} css={nameRowCss}>
          <Typography
            className={nameClassName}
            componentNode='p'
            style={nameStyle}
            variant='B3'
            {...(isNearby ? {} : { color: 'subheading' as const })}
          >
            {nameText}
          </Typography>
          {isNearby ? (
            <Container className='flex flex-middle flex-no-shrink'>
              <NearbyInfoIcon />
            </Container>
          ) : null}
        </Container>
        {isNearby ? <Container css={TUPLE_HOVER_UNDERLINE_CSS} /> : null}
      </Container>
      </RevampTooltip>
    </Container>
  )
}

export default memo(TimeRouteBlock)
